import { format, parseISO } from 'date-fns';
import { enUS, es, fr, it, pl } from 'date-fns/locale';
import { Fragment, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PromiseDateSettings } from '@/api/types/services';
import { resolveLocale } from '@/i18n';
import { computePromisePreview, formatCountdown } from '@/lib/promise-date';

const localeMap = { en: enUS, pl, fr, es, it } as const;

/** InPost mark shown inside the yellow badge (Figma 712-17873). */
function PromiseDateIcon() {
  return (
    <svg
      width={19.687}
      height={29.312}
      viewBox="0 0 20 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.43748 14.6562C7.43748 14.6562 5.77288 15.3125 3.71874 15.3125C1.6646 15.3125 0 14.6562 0 14.6562C0 14.6562 1.6646 14 3.71874 14C5.77288 14 7.43748 14.6562 7.43748 14.6562Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.25 5.68749C12.25 5.68749 10.8535 4.7519 9.76538 3.18259C8.67925 1.61129 8.3125 0 8.3125 0C8.3125 0 9.70896 0.933587 10.7971 2.50489C11.8853 4.0762 12.25 5.68749 12.25 5.68749Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.31249 9.62499C8.31249 9.62499 6.65467 9.4482 4.9631 8.60226C3.27153 7.75632 2.1875 6.5625 2.1875 6.5625C2.1875 6.5625 3.84532 6.73929 5.53688 7.58523C7.22845 8.43117 8.31249 9.62499 8.31249 9.62499Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.25 23.625C12.25 23.625 10.8535 24.5606 9.76538 26.1299C8.67925 27.6992 8.3125 29.3125 8.3125 29.3125C8.3125 29.3125 9.70896 28.3789 10.7971 26.8076C11.8853 25.2383 12.25 23.625 12.25 23.625Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.31249 19.6875C8.31249 19.6875 6.65467 19.8895 4.9631 20.8563C3.27153 21.8231 2.1875 23.1875 2.1875 23.1875C2.1875 23.1875 3.84532 22.9854 5.53688 22.0187C7.22845 21.0519 8.31249 19.6875 8.31249 19.6875Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.5539 17.8318C13.8556 21.0844 15.9479 23.4163 19.6854 23.9695C19.2591 24.026 18.8266 24.0603 18.3837 24.0624C13.0878 24.0846 8.77286 19.9901 8.75009 14.9164C8.72733 9.84266 13.0009 5.7098 18.2968 5.68759C18.7686 5.68557 19.2322 5.71787 19.6875 5.77844C15.4056 6.29934 12.5497 9.54183 11.9847 13.6686C11.8047 16.1843 14.8076 17.0343 14.8076 17.0343C14.8076 17.0343 13.5783 17.8176 12.5539 17.8338V17.8318Z"
        fill="black"
      />
    </svg>
  );
}

/** Splits a template on the __TIME__ / __DATE__ tokens, injecting styled nodes. */
function renderTemplate(template: string, vars: Record<string, ReactNode>): ReactNode {
  return template
    .split(/(__TIME__|__DATE__)/)
    .map((part, index) =>
      part === '__TIME__' || part === '__DATE__' ? (
        <Fragment key={index}>{vars[part]}</Fragment>
      ) : (
        <Fragment key={index}>{part}</Fragment>
      ),
    );
}

/** Live preview of the storefront cut-off card for the given (draft) settings. */
export function PromiseDatePreview({ settings }: { settings: PromiseDateSettings }) {
  const { t, i18n } = useTranslation('widgets');
  const locale = resolveLocale(i18n.language);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const result = computePromisePreview(settings, now);

  let body: ReactNode;
  if (!result.visible || !result.deliveryDate) {
    body = <span>{t('promiseDate.previewMessage.inactive')}</span>;
  } else {
    const dateLabel = format(parseISO(result.deliveryDate), 'EEE, d MMM', {
      locale: localeMap[locale],
    });
    const dateNode = <span className="font-semibold text-black underline">{dateLabel}</span>;

    if (result.showCountdown && result.countdownSeconds != null) {
      const timeLabel = formatCountdown(
        result.countdownSeconds,
        t('promiseDate.previewMessage.hoursShort'),
        t('promiseDate.previewMessage.minutesShort'),
      );
      const timeNode = <span className="font-semibold text-[#343434]">{timeLabel}</span>;
      if (result.isNextDay) {
        // The date is implied, so the delivery slot carries the localized phrase.
        body = renderTemplate(
          t('promiseDate.previewMessage.orderInNextDay', { time: '__TIME__', date: '__DATE__' }),
          {
            __TIME__: timeNode,
            __DATE__: (
              <span className="font-semibold text-black underline">
                {t('promiseDate.previewMessage.nextDayDelivery')}
              </span>
            ),
          },
        );
      } else {
        body = renderTemplate(
          t('promiseDate.previewMessage.orderIn', { time: '__TIME__', date: '__DATE__' }),
          {
            __TIME__: timeNode,
            __DATE__: dateNode,
          },
        );
      }
    } else {
      body = renderTemplate(t('promiseDate.previewMessage.deliveryBy', { date: '__DATE__' }), {
        __DATE__: dateNode,
      });
    }
  }

  return (
    <div
      className="flex max-w-[488px] items-center gap-[15px] rounded-[12px] bg-white p-[18px]"
      style={{ fontFamily: "'Google Sans', system-ui, -apple-system, sans-serif" }}
    >
      <span className="flex size-[45px] shrink-0 items-center justify-center rounded-[16.875px] bg-[#ffcc05]">
        <PromiseDateIcon />
      </span>
      <p className="text-[15px] leading-[22px] text-[#6e6e6e]">{body}</p>
    </div>
  );
}
